import {
  ReactNode,
  type RefObject,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { nextAttentionPath } from "./attentionItems";

/**
 * Where each form path is on screen, so the grid can travel to one.
 *
 * A registry rather than a query for the input's `name`, because not every kind
 * of control carries one the same way — a checkbox and a select render
 * differently, and a repeated row is not an input at all — and because the thing
 * worth scrolling to is the field's whole box, label and helper line included,
 * not just the box the caret sits in.
 *
 * The map lives in a ref: registering a control must not re-render the form, or
 * mounting the fields would loop.
 *
 * Distinct from `useScrollToDeepLinkTarget`, which waits for an element named by
 * the URL to appear on mount. This is imperative and already-mounted: the
 * reviewer asks for the next thing, and it is on screen or nowhere.
 */

/**
 * Whether travel focuses the registered element or the first control inside it.
 *
 * A field wants `within`: the box is what to scroll to, the input is what to
 * type in. A repeated row wants `self`, because the question travel is answering
 * is whether the row belongs at all — focusing its first cell would announce a
 * value when the reviewer needs to hear the row and what is being asked about it.
 */
export type AttentionFocus = "within" | "self";

/** Where each form path is on screen, and how travel should land on it. */
interface AttentionTargets {
  register: (path: string, element: HTMLElement | null, focus: AttentionFocus) => void;
  /** Scroll the path into view and focus what the reviewer would type into. */
  travelTo: (path: string) => void;
  /**
   * What currently wants attention, published by whoever walks the form.
   *
   * A field answering itself has no way to know what else is outstanding, and it
   * should not: the walk belongs to the form. Publishing it here is what lets a
   * control that has just been resolved hand the reviewer to the next one.
   */
  publishTravellablePaths: (paths: readonly string[]) => void;
  /** Travel to the next outstanding item after where the reviewer is. */
  travelToNextPublished: () => void;
  /** The reviewer put themselves here — a click, a tab — so travel counts from it. */
  markPosition: (path: string) => void;
  /**
   * The path travel last landed on and has not been dismissed, so the field there
   * can show why it was flagged without waiting for a pointer.
   */
  arrivedAt: string | null;
  /** The reviewer has left this path — blur, Escape, a click away. */
  dismissArrival: (path: string) => void;
  /** Paths whose disagreement with the record the reviewer has answered. */
  acknowledged: ReadonlySet<string>;
  /** "I have looked at both values and I am keeping this one." */
  acknowledge: (path: string) => void;
}

const AttentionTargetsContext = createContext<AttentionTargets | null>(null);

/**
 * The set of disagreements the reviewer has answered by keeping what the form
 * holds, owned where the attention walk is computed.
 *
 * Acknowledging is not the same as editing. Taking the record's value changes the
 * form and the disagreement is gone on its own; keeping the document's value
 * changes nothing, so without somewhere to record the decision the warning, the
 * banner count and travel would all keep insisting on a question already
 * answered. Deliberately *not* recorded as a form edit: that would mark the field
 * as corrected, and the per-field edit signal the extraction pipeline keeps for
 * prompt tuning would gain a correction nobody made.
 */
export function useReviewAcknowledgements(): {
  acknowledged: ReadonlySet<string>;
  acknowledge: (path: string) => void;
  /**
   * Answer every outstanding question at once.
   *
   * A reviewer who has read the document and is happy with all of it should not
   * have to say so sixty times. Its counterpart is that the flags exist to be
   * skippable, not to be worked through as a ritual.
   *
   * One call rather than a loop of `acknowledge`, so the set moves once and the
   * form re-renders once — sixty separate state updates on a long profile is a
   * visible stutter.
   */
  acknowledgeAll: (paths: readonly string[]) => void;
} {
  const [acknowledged, setAcknowledged] = useState<ReadonlySet<string>>(() => new Set());
  const acknowledge = useCallback((path: string) => {
    setAcknowledged((previous) => new Set(previous).add(path));
  }, []);
  const acknowledgeAll = useCallback((paths: readonly string[]) => {
    if (paths.length === 0) return;
    setAcknowledged((previous) => {
      const next = new Set(previous);
      for (const path of paths) next.add(path);
      return next;
    });
  }, []);
  return { acknowledged, acknowledge, acknowledgeAll };
}

export function AttentionTargetsProvider({
  children,
  acknowledged,
  acknowledge,
}: {
  children: ReactNode;
  acknowledged: ReadonlySet<string>;
  acknowledge: (path: string) => void;
}) {
  const targets = useRef(new Map<string, { element: HTMLElement; focus: AttentionFocus }>());
  // A ref, not state. Nothing renders from where the reviewer is — travel only
  // reads it when asked for the next stop — and holding it in state changed the
  // context's identity on every focus move, which re-rendered every control on the
  // sheet and re-registered every target ref through the callbacks below.
  const cursor = useRef<string | null>(null);
  // Separate from `cursor`, which stays put because it is where "next" counts
  // from. An arrival is a moment, and it ends as soon as the reviewer acts.
  const [arrivedAt, setArrivedAt] = useState<string | null>(null);
  // Scoped to the path being left, so the blur of the field the reviewer is
  // leaving cannot wipe the arrival `travelTo` has just set for the field they are
  // entering. Both fire on one click and React batches them, so an unconditional
  // clear only worked because blur happens to precede click in every engine —
  // which nothing here guarantees, and which jsdom does not do at all.
  const dismissArrival = useCallback(
    (path: string) => setArrivedAt((current) => (current === path ? null : current)),
    []
  );

  const register = useCallback(
    (path: string, element: HTMLElement | null, focus: AttentionFocus) => {
      if (element == null) targets.current.delete(path);
      else targets.current.set(path, { element, focus });
    },
    []
  );

  const travelTo = useCallback((path: string) => {
    cursor.current = path;
    setArrivedAt(path);
    const target = targets.current.get(path);
    if (target == null) return;
    // Only scroll when there is something to scroll to. Two flagged fields a few
    // rows apart are usually both on screen already, and recentring the second
    // one moves the whole form under the reviewer to no purpose — it reads as
    // the page lurching every time they press the key.
    if (!isFullyInView(target.element)) {
      target.element.scrollIntoView({ block: "center", behavior: "smooth" });
    }
    focusTarget(target.element, target.focus);
  }, []);

  const markPosition = useCallback((path: string) => {
    cursor.current = path;
  }, []);

  // A ref, not state: nothing renders from it, and re-rendering every field on
  // the sheet each time the walk changes would undo the memoization the field
  // controls rely on.
  const travellable = useRef<readonly string[]>([]);
  const publishTravellablePaths = useCallback((paths: readonly string[]) => {
    travellable.current = paths;
  }, []);

  const travelToNext = useCallback(
    (paths: readonly string[]) => {
      if (paths.length === 0) return;
      const from = cursor.current;
      const direct = nextAttentionPath(paths, from);
      if (direct != null) {
        travelTo(direct);
        return;
      }
      // The reviewer is sitting on a field that wants nothing, so "next" means the
      // next one *below them on the page* rather than the next in the list.
      const fromElement = from == null ? undefined : targets.current.get(from)?.element;
      const following =
        fromElement == null
          ? undefined
          : paths.find((path) => {
              const element = targets.current.get(path)?.element;
              if (element == null) return false;
              return (
                (fromElement.compareDocumentPosition(element) &
                  Node.DOCUMENT_POSITION_FOLLOWING) !==
                0
              );
            });
      travelTo(following ?? paths[0]);
    },
    [travelTo]
  );

  // Deliberately reading the list at press time rather than closing over it. A
  // field that has just been answered is still in the walk the caller last
  // published — the form has not re-rendered yet — and travel counts from the
  // cursor, so it moves past the current field either way.
  const travelToNextPublished = useCallback(
    () => travelToNext(travellable.current),
    [travelToNext]
  );

  const value = useMemo(
    () => ({
      register,
      travelTo,
      publishTravellablePaths,
      travelToNextPublished,
      markPosition,
      arrivedAt,
      dismissArrival,
      acknowledged,
      acknowledge,
    }),
    [
      register,
      travelTo,
      publishTravellablePaths,
      travelToNextPublished,
      markPosition,
      arrivedAt,
      dismissArrival,
      acknowledged,
      acknowledge,
    ]
  );

  return (
    <AttentionTargetsContext.Provider value={value}>{children}</AttentionTargetsContext.Provider>
  );
}

/**
 * The scrolling ancestor an element actually moves within.
 *
 * Not the window: the review surfaces put their form in an inner scrolling
 * column beside the document, so "on screen" is a question about that column.
 */
function scrollContainerOf(element: HTMLElement): HTMLElement | null {
  for (let node = element.parentElement; node != null; node = node.parentElement) {
    const { overflowY } = window.getComputedStyle(node);
    const scrolls = overflowY === "auto" || overflowY === "scroll";
    if (scrolls && node.scrollHeight > node.clientHeight) return node;
  }
  return null;
}

/**
 * Whether the whole element is already visible where it lives.
 *
 * An element with no box at all — never laid out, or in a test environment with
 * no layout engine — reports zeros, which would otherwise measure as trivially
 * visible and suppress every scroll. Treated as not visible, so travel behaves as
 * it always did rather than silently doing nothing.
 */
function isFullyInView(element: HTMLElement): boolean {
  const rect = element.getBoundingClientRect();
  if (rect.height === 0 && rect.width === 0) return false;

  const container = scrollContainerOf(element);
  const top = container == null ? 0 : container.getBoundingClientRect().top;
  const bottom = container == null ? window.innerHeight : container.getBoundingClientRect().bottom;

  return rect.top >= top && rect.bottom <= bottom;
}

/**
 * Put the caret where the reviewer would act.
 *
 * `preventScroll` because the smooth scroll above is already on its way, and
 * letting focus scroll as well lands the field somewhere other than the middle.
 * An element that is not focusable on its own — a row's box — needs a tabindex
 * before it can take focus at all.
 */
function focusTarget(element: HTMLElement, focus: AttentionFocus): void {
  if (focus === "within") {
    const input = element.querySelector<HTMLElement>(
      "input:not([type='hidden']), textarea, [role='combobox']"
    );
    if (input != null) {
      input.focus({ preventScroll: true });
      return;
    }
  }
  if (element.tabIndex < 0) element.tabIndex = -1;
  element.focus({ preventScroll: true });
}

/**
 * A ref callback that registers this control as the target for `path`.
 *
 * Outside a provider it is inert, so a caller rendering the field control on its
 * own — or a test that does — needs no extra setup.
 */
export function useAttentionTarget(
  path: string,
  focus: AttentionFocus = "within"
): (element: HTMLElement | null) => void {
  const context = useContext(AttentionTargetsContext);
  return useCallback(
    (element: HTMLElement | null) => context?.register(path, element, focus),
    [context, focus, path]
  );
}

/**
 * Publishes the form's travel order so a control that has just been answered can
 * hand the reviewer to the next one.
 *
 * A component rather than a call inside whatever renders the jump button: the
 * button hides when nothing is outstanding, and one surface renders it only for
 * a document still under review. Publishing from there would leave the keyboard
 * silently doing nothing on exactly the forms where the button is absent.
 *
 * Renders nothing. It exists to be unconditional.
 */
export function PublishTravelTargets({ paths }: { paths: readonly string[] }): null {
  const context = useContext(AttentionTargetsContext);
  const publish = context?.publishTravellablePaths;
  useEffect(() => publish?.(paths), [publish, paths]);
  return null;
}

/**
 * Register every named control inside a container as a travel target.
 *
 * The per-field `useAttentionTarget` ref suits a schema-driven form, which
 * renders its own controls and can put a ref on each. A hand-written form cannot:
 * it builds sixty-odd inputs through shared field components, and threading a ref
 * through every one of them to reach a registry is the kind of change that
 * touches every call site and gets skipped on the next field somebody adds.
 *
 * So this sweeps instead. react-hook-form names every control it registers, so
 * the names are already in the DOM; the element worth scrolling to is the field's
 * whole box — label and helper line included — which is the input's closest form
 * control.
 *
 * `deps` is what re-runs the sweep. A repeated group that gains or loses a row
 * changes which names exist, and a stale registry travels to a control that is no
 * longer there.
 */
export function useAttentionTargetSweep(deps: readonly unknown[]): RefObject<HTMLElement | null> {
  const containerRef = useRef<HTMLElement | null>(null);
  const context = useContext(AttentionTargetsContext);
  const register = context?.register;

  useEffect(() => {
    const container = containerRef.current;
    if (container == null || register == null) return undefined;

    const named = Array.from(container.querySelectorAll<HTMLElement>("[name]"));
    const paths: string[] = [];
    for (const element of named) {
      const path = element.getAttribute("name");
      if (path == null || path.length === 0) continue;
      const box = element.closest<HTMLElement>(".MuiFormControl-root") ?? element;
      register(path, box, "within");
      paths.push(path);
    }

    // Unregister on the way out, so a form that unmounts — a dialog closing, a
    // different document loading — cannot leave travel pointing at detached
    // elements that would silently never scroll.
    return () => {
      for (const path of paths) register(path, null, "within");
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [register, ...deps]);

  return containerRef;
}

/** Travel, and the position it counts from. Inert with no provider. */
export function useAttentionTravel(): Pick<
  AttentionTargets,
  "travelTo" | "travelToNextPublished" | "publishTravellablePaths" | "markPosition"
> {
  const context = useContext(AttentionTargetsContext);
  const noTravel = useCallback(() => {}, []);
  if (context == null) {
    return {
      travelTo: noTravel,
      travelToNextPublished: noTravel,
      publishTravellablePaths: noTravel,
      markPosition: noTravel,
    };
  }
  return {
    travelTo: context.travelTo,
    travelToNextPublished: context.travelToNextPublished,
    publishTravellablePaths: context.publishTravellablePaths,
    markPosition: context.markPosition,
  };
}

/**
 * Whether travel just landed here, and how to say the reviewer has moved on.
 * Inert outside a provider, where nothing is travelling anywhere.
 */
export function useArrival(path: string): { hasArrived: boolean; dismiss: () => void } {
  const context = useContext(AttentionTargetsContext);
  const dismiss = useCallback(() => context?.dismissArrival(path), [context, path]);
  return { hasArrived: context?.arrivedAt === path, dismiss };
}

/**
 * Whether this path's disagreement has been answered, and how to answer it.
 * Inert outside a provider, so a field control rendered on its own still works.
 */
export function useAcknowledgement(key: string | null): {
  isAcknowledged: boolean;
  acknowledge: () => void;
} {
  const context = useContext(AttentionTargetsContext);
  const acknowledge = useCallback(() => {
    if (key != null) context?.acknowledge(key);
  }, [context, key]);
  return { isAcknowledged: key != null && (context?.acknowledged.has(key) ?? false), acknowledge };
}
